import { ApiError } from "@carepass/api-client";
import { pollAsyncTask } from "@carepass/async-task";
import type { AsyncTaskStatus } from "@carepass/async-task";
import type { AdminApplicationServices } from "./providers";
import type { Schemas } from "../features/workspace/api.generated";

const FILE_STATUSES = new Set<AsyncTaskStatus>([
  "WAITING_UPLOAD",
  "PROCESSING",
  "READY",
  "FAILED",
  "EXPIRED",
]);
const TERMINAL_FILE_STATUSES = new Set<AsyncTaskStatus>(["READY", "FAILED", "EXPIRED"]);
const MAX_FILE_SIZE = 20_971_520;

export type AdminUploadPurpose = "CONTENT" | "PAYOUT_EVIDENCE" | "PARTNER_SIGNUP_IMAGE";

type FileServices = Pick<
  AdminApplicationServices,
  "apiClient" | "asyncTasks" | "polling" | "sessionLifecycle"
>;
type ValidFileOperation = Schemas["FileOperationResponse"] & { status: AsyncTaskStatus };
const activeFilePolls = new WeakMap<object, Map<string, Promise<ValidFileOperation>>>();

function createFileRequest(services: FileServices, externalSignal?: AbortSignal) {
  const request = services.sessionLifecycle.createRequestController();
  const abortFromPage = () => request.controller.abort(externalSignal?.reason);
  externalSignal?.addEventListener("abort", abortFromPage, { once: true });
  if (externalSignal?.aborted) abortFromPage();
  return {
    signal: request.controller.signal,
    release() {
      externalSignal?.removeEventListener("abort", abortFromPage);
      request.release();
    },
  };
}

function normalizedFileOperation(
  operation: Schemas["FileOperationResponse"],
): ValidFileOperation {
  if (!FILE_STATUSES.has(operation.status as AsyncTaskStatus)) {
    throw new ApiError({
      kind: "unexpected",
      code: "FILE_STATUS_INVALID",
      message: "The server returned an unsupported file operation status.",
    });
  }
  return { ...operation, status: operation.status as AsyncTaskStatus };
}

function fileOperationError(operation: Schemas["FileOperationResponse"]): ApiError {
  if (operation.status === "EXPIRED") {
    return new ApiError({
      kind: "gone",
      status: 410,
      code: operation.failureCode ?? "FILE_OPERATION_EXPIRED",
      message: operation.failureMessage ?? "The file operation expired.",
    });
  }
  return new ApiError({
    kind: "conflict",
    status: 409,
    code: operation.failureCode ?? "FILE_OPERATION_FAILED",
    message: operation.failureMessage ?? "The file operation failed.",
  });
}

async function waitForReadyFileOperation(
  services: FileServices,
  initial: Schemas["FileOperationResponse"],
  signal: AbortSignal,
): Promise<Schemas["FileOperationResponse"]> {
  const first = normalizedFileOperation(initial);
  if (TERMINAL_FILE_STATUSES.has(first.status)) {
    services.asyncTasks.remove(first.operationId);
    if (first.status !== "READY") throw fileOperationError(first);
    return first;
  }

  let polls = activeFilePolls.get(services);
  if (!polls) {
    polls = new Map();
    activeFilePolls.set(services, polls);
  }
  const activePoll = polls.get(first.operationId);
  if (activePoll) return activePoll;

  const startedAt = new Date().toISOString();
  const save = (operation: Schemas["FileOperationResponse"]) => services.asyncTasks.save({
    operationId: operation.operationId,
    scope: "admin",
    operationType: operation.operationType,
    startedAt,
    updatedAt: new Date().toISOString(),
  });
  const poll = (async () => {
    save(first);
    const result = await pollAsyncTask<ValidFileOperation>({
      signal,
      policy: { ...services.polling, backoffFactor: 1.5 },
      load: async (pollSignal) => {
        const response = await services.apiClient.get<Schemas["FileOperationResponse"]>(
          `/api/v1/admin/files/operations/${encodeURIComponent(first.operationId)}`,
          { signal: pollSignal, cache: "no-store" },
        );
        return normalizedFileOperation(response.data);
      },
      onProgress: save,
    });
    if (result.outcome === "timeout") {
      throw new ApiError({
        kind: "conflict",
        code: "FILE_OPERATION_TIMEOUT",
        message: "The file operation is still processing.",
      });
    }
    services.asyncTasks.remove(first.operationId);
    if (result.snapshot.status !== "READY") throw fileOperationError(result.snapshot);
    return result.snapshot;
  })();
  polls.set(first.operationId, poll);
  try {
    return await poll;
  } finally {
    polls.delete(first.operationId);
  }
}

export async function downloadAdminFile(
  services: FileServices,
  fileAssetId: number,
  context: Schemas["AdminFileDownloadRequest"],
  signal?: AbortSignal,
): Promise<Blob> {
  if (!Number.isSafeInteger(fileAssetId) || fileAssetId <= 0) {
    throw new ApiError({ kind: "validation", code: "FILE_ID_INVALID", message: "Invalid file ID." });
  }
  const request = createFileRequest(services, signal);
  try {
    const ticket = await services.apiClient.post<
      Schemas["FileOperationResponse"],
      Schemas["AdminFileDownloadRequest"]
    >(`/api/v1/admin/files/${fileAssetId}/download-requests`, context, {
      signal: request.signal,
      cache: "no-store",
    });
    const ready = await waitForReadyFileOperation(services, ticket.data, request.signal);
    const response = await services.apiClient.get<Blob>(
      `/api/v1/admin/files/downloads/${encodeURIComponent(ready.operationId)}`,
      { signal: request.signal, cache: "no-store", responseType: "blob" },
    );
    return response.data;
  } finally {
    request.release();
  }
}

function validateUpload(file: File, purpose: AdminUploadPurpose): Schemas["CreateFileUploadRequest"] {
  const originalFilename = file.name.trim().replaceAll("\\", "/").split("/").at(-1) ?? "";
  const contentType = file.type.trim();
  if (!originalFilename || originalFilename === "." || originalFilename === ".." || originalFilename.length > 500) {
    throw new ApiError({ kind: "validation", code: "FILE_NAME_INVALID", message: "Invalid filename." });
  }
  if (!contentType || contentType.length > 150) {
    throw new ApiError({ kind: "validation", code: "FILE_CONTENT_TYPE_INVALID", message: "Invalid content type." });
  }
  if (purpose === "PARTNER_SIGNUP_IMAGE" && !["image/png", "image/jpeg", "image/webp"].includes(contentType)) {
    throw new ApiError({ kind: "validation", code: "FILE_CONTENT_TYPE_INVALID", message: "PNG, JPEG 또는 WebP 이미지를 선택해 주세요." });
  }
  if (!Number.isSafeInteger(file.size) || file.size < 1 || file.size > MAX_FILE_SIZE) {
    throw new ApiError({ kind: "validation", code: "FILE_SIZE_INVALID", message: "Invalid file size." });
  }
  return { purpose, originalFilename, contentType, fileSize: file.size };
}

export async function uploadAdminFile(
  services: FileServices,
  file: File,
  purpose: AdminUploadPurpose,
  signal?: AbortSignal,
): Promise<Schemas["FileOperationResponse"]> {
  const metadata = validateUpload(file, purpose);
  const request = createFileRequest(services, signal);
  try {
    const ticket = await services.apiClient.post<
      Schemas["FileOperationResponse"],
      Schemas["CreateFileUploadRequest"]
    >("/api/v1/admin/files/upload-requests", metadata, {
      signal: request.signal,
      cache: "no-store",
    });
    if (!ticket.data.uploadToken) {
      throw new ApiError({
        kind: "unexpected",
        code: "UPLOAD_TOKEN_MISSING",
        message: "The server did not return an upload token.",
      });
    }
    const uploaded = await services.apiClient.put<Schemas["FileOperationResponse"], Blob>(
      `/api/v1/admin/files/upload-requests/${encodeURIComponent(ticket.data.operationId)}/content`,
      file,
      {
        signal: request.signal,
        cache: "no-store",
        headers: {
          "Content-Type": file.type,
          "X-Carepass-Upload-Token": ticket.data.uploadToken,
        },
      },
    );
    const ready = await waitForReadyFileOperation(services, uploaded.data, request.signal);
    if (!ready.fileAssetId) {
      throw new ApiError({
        kind: "unexpected",
        code: "FILE_ASSET_ID_MISSING",
        message: "The ready upload has no file asset ID.",
      });
    }
    return ready;
  } finally {
    request.release();
  }
}
